import { BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react";
import { graphqlRequestBaseQuery } from "@rtk-query/graphql-request-base-query";

import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import { decodeDescriptionsInResponse } from "~/helpers/decodeHtmlEntities";
import retryAfterPolicy from "~/helpers/retryAfterPolicy";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import client from "~/services/client";
import {
	extractRetryAfterSeconds,
	isAbortError,
	isRetryableError,
	isUnauthorizedError
} from "~/services/graphqlErrorHelpers";
import {
	addGraphqlErrorCodeBreadcrumb,
	buildGraphqlRequestContext,
	captureRtkQueryFailure
} from "~/services/sentryRtkInstrumentation";

// graphql-request-base-query strips response onto meta, which mutation trigger promises never expose — keep errors/status on the error itself.
const graphqlBaseQueryRaw = graphqlRequestBaseQuery({
	client,
	customErrors: ({ message, name, response, stack }) => ({
		message,
		name,
		response: {
			errors: response.errors,
			retryAfterSeconds: retryAfterPolicy.parseRetryAfterSeconds(response.headers.get("retry-after")),
			status: response.status
		},
		stack
	})
});

const graphqlBaseQuery: typeof graphqlBaseQueryRaw = async (args, api, extraOptions) => {
	const result = await graphqlBaseQueryRaw(args, api, extraOptions);
	if (result.data !== undefined) decodeDescriptionsInResponse(result.data);
	return result;
};

function normalizeNetworkLikeError(error: unknown): unknown {
	if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) {
		return { status: "FETCH_ERROR", error: error.message };
	}
	if (error instanceof Error && error.name === "AbortError") {
		return { status: "TIMEOUT_ERROR", error: error.message };
	}
	return error;
}

interface GraphQLRequestArgs {
	document: string | { toString(): string };
	variables?: Record<string, unknown> | void;
}

// RTK Query contract: normalize retry throws into { error } so baseQueryGraphql never throws.
async function safeRetry(
	document: GraphQLRequestArgs["document"],
	variables: GraphQLRequestArgs["variables"],
	api: Parameters<typeof graphqlBaseQuery>[1],
	extraOptions: Parameters<typeof graphqlBaseQuery>[2]
): Promise<Awaited<ReturnType<typeof graphqlBaseQuery>>> {
	try {
		return await graphqlBaseQuery({ document: String(document), variables }, api, extraOptions);
	} catch (retryError) {
		const translated = normalizeNetworkLikeError(retryError);
		return {
			error: translated as Awaited<ReturnType<typeof graphqlBaseQuery>>["error"]
		} as Awaited<ReturnType<typeof graphqlBaseQuery>>;
	}
}

// Strips meta.request (GraphQL document + variables — potentially sensitive).
// Only response.{errors,status} survives onto RTK action meta.
export const sanitizeRejectionMeta = (
	meta: unknown
): { response?: { errors?: unknown; status?: unknown } } | undefined => {
	if (!meta || typeof meta !== "object") return undefined;
	const response = (meta as { response?: unknown }).response;
	if (!response || typeof response !== "object") return undefined;
	const errors = (response as { errors?: unknown }).errors;
	const status = (response as { status?: unknown }).status;
	if (errors === undefined && status === undefined) return undefined;
	return { response: { errors, status } };
};

export const baseQueryGraphql: BaseQueryFn<GraphQLRequestArgs, unknown, unknown> = async (
	{ document, variables },
	api,
	extraOptions
) => {
	const startedAt = performance.now();
	const requestContext = buildGraphqlRequestContext(api, Config.endpoint);

	// Gate all queries on auth being ready using settings.tenantId.
	const state = api.getState() as { settings?: { tenantId: string } };
	if (state.settings !== undefined && !state.settings.tenantId) {
		return { data: undefined };
	}

	try {
		// Check and refresh token if needed before making the request
		const token = await sfAuth.checkSession();

		// No valid session (e.g. during OAuth code exchange) - skip the request
		if (!token) {
			return { data: undefined };
		}

		const result = await graphqlBaseQuery({ document: String(document), variables }, api, extraOptions);

		if (result.error && isUnauthorizedError(result.error)) {
			// wsapi's code is only on this error, which the refresh and replay below discard.
			addGraphqlErrorCodeBreadcrumb({ error: result.error, request: requestContext });

			// Force token refresh
			const refreshedToken = await sfAuth.checkSession(true);
			const resolution = await unauthorizedRetryPolicy.resolveUnauthorized(
				{ endpointName: api.endpoint, operationType: api.type, tokenRefreshed: !!refreshedToken },
				{
					redirectToLogin: () => sfAuth.redirectToLogin(),
					// async so the MaybePromise the transport returns is settled here, not by the helper.
					replay: async () => graphqlBaseQuery({ document: String(document), variables }, api, extraOptions)
				}
			);

			if (resolution.action === "redirectToLogin") {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: result.error,
					request: requestContext
				});
				return { error: normalizeNetworkLikeError(result.error), meta: sanitizeRejectionMeta(result.meta) };
			}

			if (resolution.action === "surfaceNotReplayed") {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: resolution.notReplayedError,
					request: requestContext
				});
				return { error: resolution.notReplayedError, meta: sanitizeRejectionMeta(result.meta) };
			}

			const retryResult = resolution.replayed;
			if (retryResult.error) {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: retryResult.error,
					request: requestContext
				});
				// Same PII-stripping contract as non-retry paths: only response.{errors,status}
				// survives; meta.request (document + variables) must not reach Redux state.
				return { error: normalizeNetworkLikeError(retryResult.error), meta: sanitizeRejectionMeta(retryResult.meta) };
			}
			return retryResult;
		}

		if (result.error) {
			const retryDelayMs =
				api.type === "query" && isRetryableError(result.error)
					? retryAfterPolicy.resolveRetryDelayMs(extractRetryAfterSeconds(result.error))
					: null;
			if (retryDelayMs !== null) {
				if (retryDelayMs > 0) await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
				const retryResult = await safeRetry(document, variables, api, extraOptions);
				if (!retryResult.error) {
					return retryResult;
				}
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: retryResult.error,
					request: requestContext
				});
				return { error: normalizeNetworkLikeError(retryResult.error), meta: sanitizeRejectionMeta(retryResult.meta) };
			}
			captureRtkQueryFailure({
				api,
				durationMs: performance.now() - startedAt,
				error: result.error,
				request: requestContext
			});
			return { error: normalizeNetworkLikeError(result.error), meta: sanitizeRejectionMeta(result.meta) };
		}
		return result;
	} catch (e) {
		if (isAbortError(e)) {
			return { error: { status: "TIMEOUT_ERROR", error: e instanceof Error ? e.message : "aborted" } };
		}
		if (api.type === "query" && isRetryableError(e)) {
			const retryResult = await safeRetry(document, variables, api, extraOptions);
			if (!retryResult.error) {
				return retryResult;
			}
			captureRtkQueryFailure({
				api,
				durationMs: performance.now() - startedAt,
				error: retryResult.error,
				request: requestContext
			});
			return { error: normalizeNetworkLikeError(retryResult.error), meta: sanitizeRejectionMeta(retryResult.meta) };
		}
		captureRtkQueryFailure({
			api,
			durationMs: performance.now() - startedAt,
			error: e,
			request: requestContext
		});
		return { error: normalizeNetworkLikeError(e) };
	}
};

export const baseApiWithGraphql = createApi({
	baseQuery: baseQueryGraphql,
	endpoints: () => ({}),
	refetchOnFocus: true,
	refetchOnMountOrArgChange: true,
	tagTypes: []
});

export type GraphqlEndpointBuilder = Parameters<
	Parameters<typeof baseApiWithGraphql.injectEndpoints>[0]["endpoints"]
>[0];
